import { useState, useEffect, useCallback, useRef } from 'react';
import { useServices } from '@/app/providers/ServiceContext';
import { useToast, getErrorMessage } from '@/app/providers';
import type { SyncStatusResponse, FailedSyncEntry, SyncEntityType } from '@yawi/types';
import type { PartialSyncType } from '@yawi/domain';

const POLL_INTERVAL_MS = 3000;

export function useAccessSync() {
  const { accessSyncRepo } = useServices();
  const toast = useToast();

  const [status, setStatus] = useState<SyncStatusResponse | null>(null);
  const [failedEntries, setFailedEntries] = useState<FailedSyncEntry[]>([]);
  const [failedTotal, setFailedTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [startingSync, setStartingSync] = useState(false);
  const [startingPartialSync, setStartingPartialSync] = useState<PartialSyncType | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refreshStatus = useCallback(async () => {
    try {
      const data = await accessSyncRepo.getStatus();
      setStatus(data);
      return data;
    } catch (err) {
      // Silently fail during polling
      return null;
    }
  }, [accessSyncRepo]);

  const loadFailedEntries = useCallback(
    async (page = 1, limit = 20) => {
      try {
        const result = await accessSyncRepo.getFailedEntries(page, limit);
        setFailedEntries(result.data);
        setFailedTotal(result.meta?.total ?? 0);
      } catch {
        // ignore
      }
    },
    [accessSyncRepo],
  );

  // Initial load
  useEffect(() => {
    setLoading(true);
    Promise.all([refreshStatus(), loadFailedEntries()]).finally(() => setLoading(false));
  }, [refreshStatus, loadFailedEntries]);

  // Auto-poll when sync is running
  useEffect(() => {
    if (status?.isRunning) {
      if (!pollIntervalRef.current) {
        pollIntervalRef.current = setInterval(async () => {
          const updated = await refreshStatus();
          if (updated && !updated.isRunning) {
            // Sync completed/cancelled - load failed entries
            await loadFailedEntries();
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
          }
        }, POLL_INTERVAL_MS);
      }
    } else {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [status?.isRunning, refreshStatus, loadFailedEntries]);

  const startSync = useCallback(async () => {
    setStartingSync(true);
    try {
      const result = await accessSyncRepo.startFullSync();
      toast.success(`Sync started: ${result.queued} entities queued`);
      await refreshStatus();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setStartingSync(false);
    }
  }, [accessSyncRepo, toast, refreshStatus]);

  const startPartialSync = useCallback(
    async (type: PartialSyncType) => {
      setStartingPartialSync(type);
      try {
        const result = await accessSyncRepo.startPartialSync([type]);
        toast.success(`Sync started: ${result.queued} entities queued`);
        await refreshStatus();
      } catch (err) {
        toast.error(getErrorMessage(err));
      } finally {
        setStartingPartialSync(null);
      }
    },
    [accessSyncRepo, toast, refreshStatus],
  );

  const retryAll = useCallback(
    async (entityType?: SyncEntityType) => {
      setRetrying(true);
      try {
        const result = await accessSyncRepo.retryFailed(entityType);
        toast.success(`Retrying ${result.retried} failed entries`);
        await Promise.all([refreshStatus(), loadFailedEntries()]);
      } catch (err) {
        toast.error(getErrorMessage(err));
      } finally {
        setRetrying(false);
      }
    },
    [accessSyncRepo, toast, refreshStatus, loadFailedEntries],
  );

  const retrySingle = useCallback(
    async (syncMapId: string) => {
      try {
        await accessSyncRepo.retrySingle(syncMapId);
        toast.success('Entry re-queued for sync');
        await Promise.all([refreshStatus(), loadFailedEntries()]);
      } catch (err) {
        toast.error(getErrorMessage(err));
      }
    },
    [accessSyncRepo, toast, refreshStatus, loadFailedEntries],
  );

  const cancel = useCallback(async () => {
    setCancelling(true);
    try {
      await accessSyncRepo.cancelSync();
      toast.success('Sync cancellation requested');
      await refreshStatus();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCancelling(false);
    }
  }, [accessSyncRepo, toast, refreshStatus]);

  return {
    status,
    failedEntries,
    failedTotal,
    loading,
    startingSync,
    startingPartialSync,
    retrying,
    cancelling,
    startSync,
    startPartialSync,
    retryAll,
    retrySingle,
    cancel,
    refreshStatus,
    loadFailedEntries,
  };
}
